import React, { useEffect } from 'react';
import { useProjects } from '../hooks/useProjects';
import { useProjectsLocalStorage } from '../hooks/useProjectsLocalStorage';
import {
  LICENSE_SHOW_EMPTY_STATE,
  PDT_EMPTY_STATE_TYPES,
  SUMMARY_PERIODS_DEFAULT
} from '../projects.consts';
import * as TrackingService from 'common/services/tracking.service';
import { TRACKING } from 'common/constants/constants';
import ProjectsContent from './ProjectsContent';
import { FEATURE_FLAGS_NAMES, PROJECT_STATUS } from '@linearb/linjs-common';
import ProjectsEmptyStates from '../components/ProjectsEmptyStates';
import getProjectsFavoritesService from '../services/projectsFavorites.services';
import { useHistory } from 'react-router';
import AppRouter from 'common/AppRouter';
import useSettingsService from 'components/hooks/useSettingsService';

const Projects = ({
  toggleModal,
  pmSensorAuthId,
  isCanConfigProjects,
  toggleSnackBar,
  organization,
  account,
  patchAccount,
  onlyFavorites
}) => {
  const { getSettingOrDefinitionValue } = useSettingsService();

  useEffect(() => {
    TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
      page: TRACKING.PAGES.PROJECTS
    });
  }, []);

  const history = useHistory();

  const {
    projects = [],
    selectedPeriod,
    setSelectedPeriod,
    SUMMARY,
    investmentColorsMap,
    loading,
    newProjects,
    setProjects,
    selectedTimeRange,
    setSelectedTimeRange,
    paginationHandler,
    favoriteProjectsCount,
    setFavoriteProjectsCount,
    totalProjects,
    keyword,
    setKeyword,
    emptyStateType,
    isDataFetchedByProjectId
  } = useProjects({ account });

  const { projectsEstimationFields, setProjectsEstimationFields } =
    useProjectsLocalStorage();

  const onSaveClick = ({ upsertedProject, isNew }) => {
    if (isNew) {
      setProjects([
        ...projects,
        { ...upsertedProject, status: PROJECT_STATUS.NEW }
      ]);
    } else {
      setProjects(
        projects.map(item =>
          item.id === upsertedProject.id
            ? { ...upsertedProject, status: PROJECT_STATUS.EDITED }
            : item
        )
      );
    }
    toggleModal();
  };

  const onDeleteProject = ({ id }) => {
    setProjects(projects.filter(item => item.id !== id));
    isFavoriteProject({ id }) && removeProjectFromFavorites({ id });
  };

  const licensePlanName = organization.license.plan_name;

  const upgradeEmptyStateEnabledFF = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.upgradeEmptyStateEnabled
  });
  const shouldShowUpgradeEmptyState =
    upgradeEmptyStateEnabledFF &&
    LICENSE_SHOW_EMPTY_STATE.includes(licensePlanName);
  const {
    isFavoriteProject,
    addProjectToFavorites,
    removeProjectFromFavorites
  } = getProjectsFavoritesService({
    account,
    patchAccount,
    toggleSnackBar,
    setFavoriteProjectsCount,
    favoriteProjectsCount,
    history,
    AppRouter
  });

  const shouldShowEmptyState =
    shouldShowUpgradeEmptyState ||
    emptyStateType === PDT_EMPTY_STATE_TYPES.NO_DATA;

  if (shouldShowEmptyState) {
    return (
      <ProjectsEmptyStates
        toggleModal={toggleModal}
        pmSensorAuthId={pmSensorAuthId}
        isCanConfigProjects={isCanConfigProjects}
        shouldShowUpgradeEmptyState={shouldShowUpgradeEmptyState}
        loading={loading}
        projects={projects}
        newProjects={newProjects}
        onSaveClick={onSaveClick}
        onlyFavorites={onlyFavorites}
        keyword={keyword}
      />
    );
  }

  return (
    <ProjectsContent
      projects={projects}
      favoriteProjectsCount={favoriteProjectsCount}
      toggleModal={toggleModal}
      loading={loading}
      toggleSnackBar={toggleSnackBar}
      pmSensorAuthId={pmSensorAuthId}
      isCanConfigProjects={isCanConfigProjects}
      onDeleteProject={onDeleteProject}
      organization={organization}
      SUMMARY={SUMMARY}
      investmentColorsMap={investmentColorsMap}
      selectedPeriod={
        selectedPeriod === 'I' ? SUMMARY_PERIODS_DEFAULT : selectedPeriod
      }
      setSelectedPeriod={setSelectedPeriod}
      projectsEstimationFields={projectsEstimationFields}
      setProjectsEstimationFields={setProjectsEstimationFields}
      onSaveClick={onSaveClick}
      addProjectToFavorites={addProjectToFavorites}
      removeProjectFromFavorites={removeProjectFromFavorites}
      isFavoriteProject={isFavoriteProject}
      selectedTimeRange={selectedTimeRange}
      setSelectedTimeRange={setSelectedTimeRange}
      paginationHandler={paginationHandler}
      totalProjects={totalProjects}
      keyword={keyword}
      setKeyword={setKeyword}
      emptyStateType={emptyStateType}
      isDataFetchedByProjectId={isDataFetchedByProjectId}
    />
  );
};

export default Projects;
